<script setup>
import { WdsButtonPrimary, WdsCampaignBanner } from '@wds/components';

// ### COMPOSABLES
const { t } = useI18n();
const { $tracking } = useNuxtApp();
const { isMobileOrTablet } = useDevice();
const dateTime = useDateTime();

// ### STORES
const testingStore = useTestingStore();

const isCampaignEnabled = testingStore.hasEnabledFeature('web_globalTravelDeals');
const campaignVars = testingStore.getFeatureVariables('web_globalTravelDeals');

const campaignHasActiveCarousel = campaignVars?.homepage?.showCarousel || false;
const campaignProperties = campaignVars?.homepage?.properties || [];

// COMPUTED
const getCampaignValidProperties = computed(() => {
  // Filter out properties that are not valid
  const filteredProperties = campaignProperties.filter((property) => {
    const isValidFrom = property?.validFrom?.match(/\d{4}-\d{2}-\d{2}/) || false;
    const isValidTo = property?.validTo?.match(/\d{4}-\d{2}-\d{2}/) || false;

    return !isValidFrom || !isValidTo || dateTime.isDateWithinInterval(null, property.validFrom, property.validTo);
  });

  // If no valid properties, return empty array
  if (!filteredProperties.length) {
    return [];
  }

  // Rotate properties on a daily basis
  const daysSinceEpoch = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  const rotationIndex = daysSinceEpoch % filteredProperties.length;

  return [...filteredProperties.slice(rotationIndex), ...filteredProperties.slice(0, rotationIndex)];
});

const showCampaignCarousel = computed(() => {
  return isCampaignEnabled && campaignHasActiveCarousel && getCampaignValidProperties.value.length > 0;
});

// METHODS
function getUrlToGlobalTravelDeals(tcs) {
  return `https://global.hostelworld.com/global-travel-deals${tcs ? '#tcs' : ''}`;
}

function handleBannerClick() {
  $tracking.triggerCampaignBannerEventAction({ action: 'Banner Clicked' });
}

function handleTermsAndConditionsClick() {
  $tracking.triggerCampaignBannerEventAction({ action: 'Terms And Conditions Clicked' });
}

function handleAllDealsClick() {
  $tracking.triggerCampaignBannerEventAction({ action: 'All Deals Clicked' });
}

function handlePropertyCardClick(property, index) {
  if (property?.propertyId) {
    $tracking.triggerCampaignBannerEventAction({
      action: 'Carousel Clicked',
      carousel_property_clicked: property?.propertyId,
      carousel_offer_displayed: property?.tag,
      carousel_position_clicked: index + 1,
    });
  }
}
</script>

<template>
  <div
    v-if="isCampaignEnabled"
    class="campaign-banner"
  >
    <template v-if="showCampaignCarousel">
      <div class="campaign-carousel">
        <h2>{{ $t('t_SDDEALSTITLE') }}</h2>
        <p class="text">
          <span>{{ $t('t_SDDEALSSUBTITLE') }}</span>
          <a
            :href="getUrlToGlobalTravelDeals(true)"
            class="tclink"
            rel="noopener"
            :target="isMobileOrTablet ? '_self' : '_blank'"
            @click="handleTermsAndConditionsClick"
          >{{ $t('t_TCAPPLY') }}</a>
        </p>
        <PromotionsHwPopularDealsCards
          :properties="getCampaignValidProperties"
          @property-click="handlePropertyCardClick"
        />
        <WdsButtonPrimary
          :href="getUrlToGlobalTravelDeals(false)"
          :text="t('t_DISCOVERALLDEALS')"
          :title="t('t_DISCOVERALLDEALS')"
          :aria-label="t('t_DISCOVERALLDEALS')"
          :reversed="true"
          rel="noopener"
          :target="isMobileOrTablet ? '_self' : '_blank'"
          icon-end="chevron-right"
          class="campaign-view-all"
          @click="handleAllDealsClick"
        />
      </div>
    </template>
    <template v-else>
      <WdsCampaignBanner
        bg-image="https://a.hwstatic.com/image/upload/v1763024138/campaign/2025/global-bg-wide.png"
        :link="getUrlToGlobalTravelDeals(false)"
        :title="$t('t_SDDEALSTITLE')"
        title-tag-color="white"
        :subtitle="$t('t_SDDEALSSUBTITLE')"
        subtitle-color="white"
        :tc-link="getUrlToGlobalTravelDeals(true)"
        :tc-text="$t('t_TCAPPLY')"
        :button-text="$t('t_DISCOVERALLDEALS')"
        class="campaign-wide"
        @tc-click="handleTermsAndConditionsClick"
        @banner-click="handleBannerClick"
        @button-click="handleAllDealsClick"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.campaign-banner {
  margin-bottom: $wds-spacing-xxl;

  .campaign-carousel {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-s;
    background-image: url('https://a.hwstatic.com/image/upload/v1763024138/campaign/2025/global-bg-small.png');
    background-repeat: no-repeat;
    background-position: left center;
    background-size: cover;
    min-height: wds-rem(510px);
    border-radius: $wds-spacing-m;
    padding: $wds-spacing-l 0;
    line-height: $wds-line-height-body-2;

    h2 {
      @include title-3-bld;

      background-color: $wds-color-white;
      color: $wds-color-pink;
      overflow-wrap: break-word;
      width: fit-content;
      clip-path: polygon(0 0, 100% 0, calc(100% - 4px) 100%, 0 100%);
      padding: $wds-spacing-xs $wds-spacing-l;
      margin-right: $wds-spacing-l;
    }

    .text {
      @include body-2-bld;

      color: $wds-color-white;
      z-index: 1;
      display: inline;
      width: fit-content;
      padding-inline: $wds-spacing-l;
    }

    .tclink {
      color: $wds-color-white;
      text-decoration: underline;
      cursor: pointer;
      padding: $wds-spacing-s;

      &:focus-within,
      &:focus,
      &:focus-visible,
      &:active {
        border-radius: $wds-spacing-s;
        box-shadow:
          wds-rem(1px) 0 0 wds-rem(1px) $wds-color-white,
          wds-rem(-1px) 0 0 wds-rem(1px) $wds-color-white,
          wds-rem(1px) 0 0 wds-rem(2px) $wds-color-ink-darker,
          wds-rem(-1px) 0 0 wds-rem(2px) $wds-color-ink-darker;
      }
    }

    .campaign-view-all {
      width: calc(100% - 2 * $wds-spacing-l);
      align-self: center;
    }

    @include tablet {
      background-image: url('https://a.hwstatic.com/image/upload/v1763024138/campaign/2025/global-bg-large.png');
    }

    @include desktop {
      h2 {
        @include title-1-bld;
      }

      .campaign-view-all {
        width: fit-content;
        margin-right: $wds-spacing-l;
        align-self: flex-end;
      }
    }
  }

  .campaign-wide {
    :deep(.tag-text) {
      color: $wds-color-pink !important;
    }
  }
}
</style>
